
import { mapGetters } from 'vuex';

export default {
  name: 'STooltip',
  model: {
    prop: 'toggleTooltip',
    event: 'triggerToggleTooltip',
  },
  props: {
    top: {
      type: Boolean,
      default: false,
    },
    right: {
      type: Boolean,
      default: false,
    },
    bottom: {
      type: Boolean,
      default: false,
    },
    left: {
      type: Boolean,
      default: false,
    },
    toggleTooltip: {
      type: Boolean,
      required: true,
    },
    margin: {
      type: Number,
      default: 10,
    },
    waitClose: {
      type: Boolean,
      default: false,
    },
    container: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      timeoutId: null,
      position: null,
      dataWait: false,
      currentPosition: null,
    };
  },
  computed: {
    ...mapGetters({
      getHasHover: 'screen/getHasHover',
      getWindowHeight: 'screen/getWindowHeight',
      getWindowWidth: 'screen/getWindowWidth',
      isMobile: 'screen/getIsMobile',
    }),
    tooltipClass() {
      return `${this.currentPosition} tooltip-${this.toggleTooltip ? 'visible' : 'hidden'}`;
    },
    getWindowSize() {
      return `${this.getWindowHeight} - ${this.getWindowWidth}`;
    },
  },
  watch: {
    waitClose() {
      this.dataWait = this.waitClose;
    },
    dataWait() {
      if (this.getHasHover && !this.isMobile) {
        clearTimeout(this.timeoutId);
        this.timeoutId = setTimeout(() => {
          if (!this.dataWait) this.close();
        }, 250);
      }
    },
    getWindowSize() {
      // Check the available space when resizing
      this.availableSpace();
    },
  },
  mounted() {
    // Position of the component passed by the props
    if (this.top && this.right && !this.bottom && !this.left) {
      this.position = 'top-right';
    } else if (!this.top && this.right && !this.bottom && !this.left) {
      this.position = 'right';
    } else if (!this.top && this.right && this.bottom && !this.left) {
      this.position = 'bottom-right';
    } else if (!this.top && !this.right && this.bottom && !this.left) {
      this.position = 'bottom';
    } else if (!this.top && !this.right && this.bottom && this.left) {
      this.position = 'bottom-left';
    } else if (!this.top && !this.right && !this.bottom && this.left) {
      this.position = 'left';
    } else if (this.top && !this.right && !this.bottom && this.left) {
      this.position = 'top-left';
    } else {
      this.position = 'top';
    }

    // Check the available space:
    // the tooltip is added to the DOM with the 'visibility: hidden' property
    this.availableSpace();
  },
  methods: {
    open() {
      this.availableSpace();
      this.$emit('trigger-toggle-tooltip', true);
    },
    close() {
      this.$emit('trigger-toggle-tooltip', false);
    },
    mouseOpen() {
      if (this.getHasHover && !this.isMobile && !this.dataWait) {
        this.dataWait = true;
        this.open();
      }
    },
    mouseDelayClose() {
      if (this.getHasHover && !this.isMobile) {
        this.dataWait = false;
        clearTimeout(this.timeoutId);
        this.timeoutId = setTimeout(() => {
          if (!this.dataWait) this.close();
        }, 250);
      }
    },

    availableSpace() {
      const activatorWidth = this.$refs.activator.offsetWidth;
      const activatorHeight = this.$refs.activator.offsetHeight;
      const tooltipWidth = this.$refs.tooltip.offsetWidth;
      const tooltipHeight = this.$refs.tooltip.offsetHeight;

      // Important: the size of the arrow must be a multiple of 2
      const arrowSize = 8;

      if (document.querySelector(this.container)) {
        // Check the available space according to the position of the container and the activator
        const containerRect = document.querySelector(this.container).getBoundingClientRect();
        const activatorRect = this.$refs.activator.getBoundingClientRect();

        // Calculates the space around the activator
        const topSpace = activatorRect.top - containerRect.top;
        const rightSpace = containerRect.right - activatorRect.right;
        const bottomSpace = containerRect.bottom - activatorRect.bottom;
        const leftSpace = activatorRect.left - containerRect.left;

        // Calculation of the difference between the available space and the dimensions of the tooltip
        // taking into account the margin and the width of the arrow
        const top = topSpace - (tooltipHeight + this.margin + arrowSize);
        const right = rightSpace - (tooltipWidth + this.margin + arrowSize);
        const bottom = bottomSpace - (tooltipHeight + this.margin + arrowSize);
        const left = leftSpace - (tooltipWidth + this.margin + arrowSize);

        // Calculates the difference between the available space and the dimensions of the tooltip
        // taking into account that it is positioned at the center of the activator
        const topSide = (topSpace + (activatorHeight / 2)) - (tooltipHeight / 2);
        const rightSide = (rightSpace + (activatorWidth / 2)) - (tooltipWidth / 2);
        const bottomSide = (bottomSpace + (activatorHeight / 2)) - (tooltipHeight / 2);
        const leftSide = (leftSpace + (activatorWidth / 2)) - (tooltipWidth / 2);

        // Check the available positions
        const positions = [
          { position: 'top', available: top > 0 && rightSide > 0 && leftSide > 0 },
          { position: 'top-right', available: right > 0 && top > 0 },
          { position: 'right', available: right > 0 && topSide > 0 && bottomSide > 0 },
          { position: 'bottom-right', available: right > 0 && bottom > 0 },
          { position: 'bottom', available: bottom > 0 && rightSide > 0 && leftSide > 0 },
          { position: 'bottom-left', available: left > 0 && bottom > 0 },
          { position: 'left', available: left > 0 && topSide > 0 && bottomSide > 0 },
          { position: 'top-left', available: left > 0 && top > 0 },
        ];

        const availablesPositions = positions.filter((pos) => pos.available === true);

        if (availablesPositions.length) {
          // Check if the requested position is available, if not, take the first available position
          const currentPositionAvailable = availablesPositions.some((pos) => pos.position === this.position && pos.available === true);
          this.currentPosition = currentPositionAvailable ? this.position : availablesPositions[0].position;
        } else {
          // If no position available, set the requested position by default
          this.currentPosition = this.position;
        }
      } else {
        // If the container does not exist, set the requested position by default
        this.currentPosition = this.position;
      }

      // Calculate the tooltip position in CSS
      let topPosition;
      let leftPosition;
      if (this.currentPosition === 'top-right') {
        topPosition = (-(tooltipHeight + this.margin) + activatorHeight / 2) + arrowSize;
        leftPosition = activatorWidth + this.margin;
      } else if (this.currentPosition === 'right') {
        topPosition = (activatorHeight - tooltipHeight) / 2;
        leftPosition = activatorWidth + this.margin;
      } else if (this.currentPosition === 'bottom-right') {
        topPosition = (activatorHeight + this.margin - activatorHeight / 2) - arrowSize;
        leftPosition = activatorWidth + this.margin;
      } else if (this.currentPosition === 'bottom') {
        topPosition = activatorHeight + this.margin;
        leftPosition = (activatorWidth - tooltipWidth - arrowSize) / 2;
      } else if (this.currentPosition === 'bottom-left') {
        topPosition = ((activatorHeight + this.margin) / 2) - arrowSize;
        leftPosition = -(tooltipWidth + this.margin + arrowSize);
      } else if (this.currentPosition === 'left') {
        topPosition = (activatorHeight - tooltipHeight) / 2;
        leftPosition = -(tooltipWidth + this.margin + arrowSize);
      } else if (this.currentPosition === 'top-left') {
        topPosition = (-(tooltipHeight + this.margin) + activatorHeight / 2) + arrowSize;
        leftPosition = -(tooltipWidth + this.margin + arrowSize);
      } else if (this.currentPosition === 'top') {
        topPosition = -(tooltipHeight + this.margin);
        leftPosition = (activatorWidth - tooltipWidth - arrowSize) / 2;
      }

      this.$refs.tooltip.style = `top: ${topPosition}px; left: ${leftPosition}px;`;
    },
  },
};
